import { useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import { isNotNil } from 'rambda';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { useTypedParams, useTypedState } from 'react-router-typesafe-routes';
import AppCell from '@/components/common/app-cell';
import Linkable from '@/components/common/linkable';
import Toggle from '@/components/common/toggle';
import GroupDetailImage from '@/components/groups/group-detail-image';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import useClearSession from '@/hooks/conversations/use-clear-session';
import useGroupInfo, { generateGroupInfoQueryOptions } from '@/hooks/groups/use-group-info';
import useGroupMembers from '@/hooks/groups/use-group-members';
import useQuitGroup from '@/hooks/groups/use-quit-group';
import useUpdateGroupDetails from '@/hooks/groups/use-update-group-details';
import useSessionSettings from '@/hooks/sessions/use-session-settings';
import useUpdateSessionDnd from '@/hooks/sessions/use-update-session-dnd';
import useUpdateSessionTop from '@/hooks/sessions/use-update-session-top';
import { useIsDesktop } from '@/hooks/use-user-platform';
import ROUTES from '@/routes';
import { useConversationSessionStore } from '@/stores/conversation-session';
import { makePrompt } from '@/stores/prompter';
import cn from '@/utils/merge-class-names';
import { generateSessionKey } from '@/utils/session';

type GroupSettingHeaderProps = {
  groupId: number;
  groupName: string;
  canManage?: boolean;
  className?: string;
  avatarClassName?: string;
};

export function GroupSettingHeader({ groupName, canManage = false, groupId, className }: GroupSettingHeaderProps) {
  return (
    <AppCell
      label={groupName}
      labelWrapperClassName='overflow-hidden whitespace-normal'
      labelClassName='line-clamp-1 break-all w-full font-bold'
      className={cn('pt-0 desktop:gap-6', className)}
      chevron={canManage}
      prefix={
        <GroupDetailImage
          groupId={groupId}
          className='size-20'
        />
      }
    />
  );
}

export default function GroupSettingsByIdIndexRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id: groupId } = useTypedParams(ROUTES.MESSAGES.GROUPS.SETTINGS);
  const { from: navigatedFrom } = useTypedState(ROUTES.MESSAGES.GROUPS.SETTINGS);
  const isDesktop = useIsDesktop();

  const { data: groupInfo, isAdmin, isChannel, canManage, isPublicGroup } = useGroupInfo(groupId);
  const { members } = useGroupMembers(groupId);
  const { session: groupSession, refetch } = useSessionSettings('group', groupId);
  const { mutateAsync: quitGroup } = useQuitGroup(groupId, navigatedFrom === 'comm');
  const { mutateAsync: updateGroup, isPending: isUpdatingGroup } = useUpdateGroupDetails(groupId);
  const { mutateAsync: setDoNotDisturb, isPending: isUpdatingDnd } = useUpdateSessionDnd('group', groupId);
  const { clearSession } = useClearSession('group', groupId);
  const { mutateAsync: updateSessionTop, isPending: isUpdatingSessionTop } = useUpdateSessionTop('group', groupId);

  const handleUpdateJoinType = async (type: 1 | 0) => {
    await updateGroup({ JoinType: type });
    queryClient.setQueryData(generateGroupInfoQueryOptions(groupId).queryKey, (data) =>
      produce(data, (draft) => {
        if (draft?.GroupData) {
          draft.GroupData.JoinType = type;
        }
      }),
    );
  };

  const handleClearMessages = () =>
    makePrompt({
      key: 'clear-group-messages',
      title: t('conversation.setting.group.clear-message.title'),
      content: t('conversation.setting.group.clear-message.content'),
      promise: async () => {
        await clearSession(false);
        useConversationSessionStore
          .getState()
          .setLastReadNotice(generateSessionKey('group', groupId), groupInfo?.GroupData?.Info);
      },
    }).catch(console.error);

  const handleQuitGroup = () =>
    makePrompt({
      key: 'quit-group',
      title: isAdmin ? t('conversation.setting.group.quit.admin-title') : t('conversation.setting.group.quit.title'),
      resolveButtonLabel: isAdmin
        ? t('conversation.setting.group.quit.admin-ok')
        : t('conversation.setting.group.quit.ok'),
      promise: async () => {
        const result = await quitGroup();

        if (result.Code === 0) {
          navigate(ROUTES.MESSAGES.$path());
        }
      },
    });

  return (
    <PageLayout
      className='gap-0 desktop:h-fit'
      title={t('conversation.setting.group.title')}
      headerConfig={{
        navigateBack: true,
        className: 'desktop:hidden',
      }}>
      <div className='pt-4 desktop:pt-0 flex-1 overflow-y-auto flex flex-col basis-50dvh justify-start'>
        <Linkable
          className='w-full desktop:hidden'
          to={canManage ? ROUTES.MESSAGES.GROUPS.SETTINGS.$.ADMIN.INFO.$path({ relative: true }) : undefined}>
          <GroupSettingHeader
            groupId={groupId}
            groupName={groupInfo?.GroupData?.GroupName ?? ''}
            canManage={canManage}
          />
        </Linkable>

        {!isChannel && (
          <>
            <Link
              to={ROUTES.MESSAGES.GROUPS.SETTINGS.$.MEMBERS.$path({ relative: true })}
              className='desktop:hidden'>
              <AppCell
                label={t('conversation.setting.group.group-member')}
                content={t('conversation.setting.group.member-count', { count: Object.keys(members).length })}
                chevron
              />
            </Link>
            <hr className='border-surface border-t desktop:hidden mx-4 my-2' />

            <AppCell
              className='desktop:hidden'
              label={t('conversation.setting.group.id')}
              content={groupInfo?.GroupData?.GroupCode.toString() ?? ''}
            />

            <Link to={ROUTES.MESSAGES.GROUPS.MEDIA.$buildPath({ params: { id: groupId } })}>
              <AppCell
                chevron={!isDesktop}
                label={t('conversation.media.title')}
              />
            </Link>

            <hr className='border-surface border-t desktop:mx-0 mx-4 my-2' />
          </>
        )}

        {canManage && !isChannel && (
          <>
            <Link to={ROUTES.MESSAGES.GROUPS.SETTINGS.$.ADMIN.APPLICATIONS.$path({ relative: true })}>
              <AppCell
                label={t('conversation.setting.group.applications')}
                content={
                  isNotNil(groupInfo?.GroupJoinApplyCount) &&
                  groupInfo.GroupJoinApplyCount > 0 && (
                    <span
                      className={cn(
                        'badge badge-accent bg-red-500 text-neutral-0 border-0 badge-xs size-4 text-[10px]',
                        groupInfo.GroupJoinApplyCount > 99 && 'w-6',
                      )}>
                      {groupInfo.GroupJoinApplyCount > 99 ? '99+' : groupInfo.GroupJoinApplyCount}
                    </span>
                  )
                }
                chevron={!isDesktop}
              />
            </Link>

            <Link to={ROUTES.MESSAGES.GROUPS.SETTINGS.$.ADMIN.$path({ relative: true })}>
              <AppCell
                label={t('conversation.setting.group.management')}
                chevron={!isDesktop}
              />
            </Link>

            <AppCell
              label={t('conversation.setting.group.public-group')}
              suffix={
                <Toggle
                  checked={isPublicGroup}
                  isLoading={isUpdatingGroup}
                  onChange={(value) => handleUpdateJoinType(value ? 0 : 1)}
                />
              }
            />

            <hr className='border-surface border-t desktop:mx-0 mx-4 my-2' />
          </>
        )}

        {!isChannel && (
          <Link to={ROUTES.MESSAGES.GROUPS.SETTINGS.$.QR_CODE.$path({ relative: true })}>
            <AppCell
              label={t('conversation.setting.group.qr-code.title')}
              chevron={!isDesktop}
            />
          </Link>
        )}

        {!isChannel && (
          <Link to={ROUTES.MESSAGES.GROUPS.SETTINGS.$.ANNOUNCEMENT.$path({ relative: true })}>
            <AppCell
              label={t('conversation.setting.group.announcement')}
              chevron={!isDesktop}
            />
          </Link>
        )}

        <hr className='border-surface border-t mx-4 my-2 desktop:mx-0' />
        <Link to={ROUTES.MESSAGES.GROUPS.SEARCH.$buildPath({ params: { id: groupId } })}>
          <AppCell
            label={t('conversation.setting.search')}
            chevron={!isDesktop}
          />
        </Link>

        <hr className='border-surface border-t mx-4 my-2 desktop:mx-0' />

        {!isChannel && (
          <Link to={ROUTES.MESSAGES.GROUPS.SETTINGS.$.NICKNAME.$path({ relative: true })}>
            <AppCell
              label={t('conversation.setting.group.nickname')}
              chevron={!isDesktop}
            />
          </Link>
        )}

        <AppCell
          label={t('conversation.setting.mute')}
          suffix={
            <Toggle
              checked={groupSession?.IsNotDisturb ?? false}
              isLoading={isUpdatingDnd}
              onChange={(value) => setDoNotDisturb(value).then(() => refetch())}
            />
          }
        />
        <AppCell
          label={t('conversation.setting.pin')}
          suffix={
            <Toggle
              checked={groupSession?.IsTop ?? false}
              isLoading={isUpdatingSessionTop}
              onChange={(value) => updateSessionTop(value).then(() => refetch())}
            />
          }
        />

        <hr className='border-surface border-t mx-4 my-2 desktop:mx-0' />

        <AppCell
          label={t('conversation.setting.clear-all')}
          onClick={handleClearMessages}
        />

        {!isChannel && (
          <>
            <hr className='border-surface border-t mx-4 my-2 desktop:mx-0' />

            <Link to={ROUTES.MESSAGES.GROUPS.REPORT.$buildPath({ params: { id: groupId } })}>
              <AppCell
                label={t('conversation.setting.report')}
                chevron={!isDesktop}
              />
            </Link>

            <hr className='border-surface border-t mx-4 my-2 desktop:mx-0' />

            <AppCell
              labelClassName='text-red-500'
              label={isAdmin ? t('conversation.setting.group.disband') : t('conversation.setting.group.leave')}
              onClick={handleQuitGroup}
            />
          </>
        )}
      </div>
    </PageLayout>
  );
}
